import React, { useState, useEffect, useReducer } from "react";

import useGlobalSearch from "hooks/useGlobalSearch";
import OutsideAlerter from "components/outsideAlerter";
import RoundLoader from "components/roundLoader";
import { navigateTo } from "utils/redirection";
import defineTimeout from "utils/defineTimeout";
import { API_FETCH_STATUS, KEY } from "constants/common";
import { isMobile } from "utils/deviceType";
import MergeStateReducer from "utils/mergeStateReducer";

import RecentSearch from "../recentSearch/recentSearch";
import SuggestedSearch from "../suggestedSearch/suggestedSearch";
import TypeAheadSearch from "../typeAheadSearch/typeAheadSearch";
import SearchFeedback from "../searchFeedback/searchFeedback";
import { faOnIncompleteSearch, faOnSearchClick, faOnSearchLoad, faOnSearchSuccess } from "../services/analytics";
import styles from "./globalSearch.scss";

const {
  global_search_b,
  search,
  round_loader,
  search_active,
  left_arrow,
  search_dropdown,
  search_field,
  search_area,
  search_icon,
  content_scroller,
  issue_text,
  search_icon_box,
  opps_icon,
  tour_overlay,
  tour_help,
  tour_help_title,
  tour_help_description,
} = styles;

const { LOADING, ERROR } = API_FETCH_STATUS;
const variant = "Variant B"

export default function GlobalSearch({
  showGlobalSearchTour,
  onGlobalSearchTourDismiss,
  showNumericSearchTour,
  onNumericSearchTourDismiss,
  lob,
  partyDetails,
  profile,
  product,
}) {
  const mobile = isMobile();

  const INITIAL_STATE = {
    searchQuery: "",
    selectedSearch: null,
    isResultsOpen: false,
    cursor: 0,
  };

  const [state, setState] = useReducer(MergeStateReducer, INITIAL_STATE);
  const [autoFill, setAutoFill] = useState(true);

  const { searchQuery, selectedSearch, isResultsOpen } = state;

  useEffect(() => {
    faOnSearchLoad(variant);
    if (mobile) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      if (mobile) return;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (isResultsOpen) {
      faOnSearchClick(variant);
      document.body.classList.add(search_active);
    } else {
      setState({ ...INITIAL_STATE, isResultsOpen });
      clearSearch();
      document.body.classList.remove(search_active);
    }
  }, [isResultsOpen]);

  const { boostSearch, setTypeAheadSearch, typeAheadSearch, suggestedSearch, recentSearch, clearSearch } = useGlobalSearch();

  const closeTour = () => {
    if (showGlobalSearchTour) {
      onGlobalSearchTourDismiss();
    }
    if (showNumericSearchTour) {
      onNumericSearchTourDismiss();
    }
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === KEY.ESCAPE.keyCode) {
      setState({ isResultsOpen: false });
    }
    if (e.keyCode === KEY.FORWARD_SLASH.keyCode) {
      setState({ isResultsOpen: true });
    }
  };

  const handleSearchSelect = ({ event, type, search }) => {
    event.preventDefault();
    faOnSearchSuccess({ type, search: search.title, searchQuery });
    setTypeAheadSearch({ contents: [] });
    setState({ isResultsOpen: false });
    const boostSearchPromise = boostSearch(search, type);
    defineTimeout(boostSearchPromise).finally(() => {
      const url = search.handlerId.replace(/<invoiceId>|<billableId>|<srNumber>|<inventoryId>|<orderRefNumber>/gi, searchQuery);
      navigateTo(url, event.ctrlKey || event.metaKey);
    });
  };

  const handleSearchQueryChange = (event) => {
    setState({ searchQuery: event.target.value });
    setState({ selectedSearch: null });
  };

  const handleSearchClick = () => {
    closeTour();
    if (!isResultsOpen) {
      setState({ isResultsOpen: true });
    }
  };

  const handleOutsideClick = () => {
    if (searchQuery) {
      faOnIncompleteSearch({ searchQuery });
    }
    setState({ isResultsOpen: false });
    if (showGlobalSearchTour) {
      onGlobalSearchTourDismiss();
    }
    if (showNumericSearchTour) {
      onNumericSearchTourDismiss();
    }
  };

  const isLoading =
    (typeAheadSearch.state === LOADING && !typeAheadSearch.contents && searchQuery.length) ||
    suggestedSearch.state === LOADING ||
    recentSearch.state === LOADING;
  const isError = typeAheadSearch.state === ERROR || suggestedSearch.state === ERROR || recentSearch.state === ERROR;

  return (
    <div className={`${global_search_b} ${isResultsOpen ? search_active : ""}`}>
      <OutsideAlerter onOutsideClick={handleOutsideClick}>
        <i className={`aeh-icon-outlined-search-icon ${search_icon}`} onClick={() => setState({ isResultsOpen: true })} />
        <div className={search}>
          <div className={showGlobalSearchTour || showNumericSearchTour ? tour_overlay : ""} onClick={closeTour}>
            {showGlobalSearchTour && (
              <div className={tour_help}>
                <div className={tour_help_title}>Global Search</div>
                <div className={tour_help_description}>Looking for something? Try searching for it here.</div>
              </div>
            )}
            {showNumericSearchTour && (
              <div className={tour_help}>
                <div className={tour_help_description}>
                  Search here for your connection, service request, invoices and other features..
                </div>
              </div>
            )}
            <div className={search_area}>
              <input
                type="text"
                placeholder="Search for product, service, support etc..."
                className={search_field}
                value={selectedSearch ? selectedSearch.title : searchQuery}
                onChange={handleSearchQueryChange}
                onClick={handleSearchClick}
                autoCapitalize={false}
                autoCorrect={false}
                autoComplete={false}
                readOnly={autoFill}
                onFocus={() => setAutoFill(false)}
              />
              <div className={search_icon_box}>
                <i className={`aeh-icon-outlined-search-icon ${search_icon}`} />
              </div>
              <i className={`aeh-icon-outlined-arrow-left ${left_arrow}`} onClick={() => setState({ isResultsOpen: false })} />
            </div>
          </div>
          {isResultsOpen && (
            <div className={search_dropdown}>
              <div className={content_scroller}>
                {searchQuery.trim().length > 0 && (
                  <TypeAheadSearch searchQuery={searchQuery} handleSearchSelect={handleSearchSelect} />
                )}
                {searchQuery.trim().length === 0 && <RecentSearch handleSearchSelect={handleSearchSelect} />}
                {(searchQuery.trim().length === 0 || typeAheadSearch.contents?.length === 0) && (
                  <SuggestedSearch handleSearchSelect={handleSearchSelect} />
                )}
              </div>
              {isLoading && (
                <div className={round_loader}>
                  <RoundLoader />
                </div>
              )}
              {isError && (
                <div className={issue_text}>
                  <i className={`aeh-icon-outlined-Info-icon ${opps_icon}`}></i> oops! there is some issue
                </div>
              )}
              <SearchFeedback {...{lob, partyDetails, profile, product}} />
            </div>
          )}
        </div>
      </OutsideAlerter>
    </div>
  );
}
